import { PiazzaInputContext } from 'ComponentLibrary/src/molecules/Form/PiazzaInputContext'
import { Autocomplete as PiazzaAutocomplete } from 'ComponentLibrary/src/piazza/forms'
import { ReactNode, useContext, useEffect, useState } from 'react'
import { Select } from 'ComponentLibrary/src/atoms/Select'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { PlusCircle } from 'react-feather'
import { TextWithEllipsis } from 'ComponentLibrary/src/molecules/TextWithEllipsis'
import { CostCenter } from 'ComponentLibrary/src/molecules/CostCenter/types'
import { CostCenterForm, type CostCenterFormLabels } from 'ComponentLibrary/src/molecules/CostCenter/CostCenterForm'
import { joinDefinedStrings } from 'ComponentLibrary/src/common/strings'

type CostCenterSelectProps = {
    defaultValue: CostCenter | 'new' | ''
    error?: boolean
    options: CostCenter[]
    selectCostCenter: (costCenterId?: string) => void
    createNewCostCenter: (costCenter: Omit<CostCenter, 'uuid'>) => void
    setIsCostCenterSelectOpen?: (isOpen: boolean) => void
    setIsCreatingNewCostCenter?: (isCreatingNewCostCenter: boolean) => void
    copy?: Partial<CostCenterSelectCopy>
}

export type CostCenterSelectCopy = {
    inputLabel: string
    addNewLabel: string
    formLabels: CostCenterFormLabels
}

const DEFAULT_COST_CENTER_SELECT_COPY: CostCenterSelectCopy = {
    inputLabel: 'Kostenstelle auswählen',
    addNewLabel: 'Neue Kostenstelle hinzufügen',
    formLabels: {
        name: 'Name Kostenstelle',
        description: 'Kurzbeschreibung',
    },
}

export function CostCenterSelect({
    options,
    error,
    selectCostCenter,
    createNewCostCenter,
    defaultValue,
    setIsCostCenterSelectOpen,
    setIsCreatingNewCostCenter,
    copy,
}: CostCenterSelectProps) {
    const theme = useTheme()
    const usePiazzaInputs = useContext(PiazzaInputContext)
    const resolvedCopy = { ...DEFAULT_COST_CENTER_SELECT_COPY, ...copy }
    const formLabels = { ...DEFAULT_COST_CENTER_SELECT_COPY.formLabels, ...copy?.formLabels }
    const stringifyValue = (value?: CostCenter | string) => (typeof value === 'string' ? value : JSON.stringify(value))

    const [isAddingNewCostCenter, setIsAddingNewCostCenter] = useState(defaultValue === 'new')

    const [value, setValue] = useState<string>(stringifyValue(defaultValue))

    // when we add a new default value, we rerender the select with the new default value
    useEffect(() => {
        setValue(stringifyValue(defaultValue))
    }, [defaultValue])

    // we can let the parent know that the form for adding a new cost center is open, without giving them control
    useEffect(() => {
        setIsCreatingNewCostCenter?.(isAddingNewCostCenter)
    }, [setIsCreatingNewCostCenter, isAddingNewCostCenter])

    const closeSelect = () => setIsCostCenterSelectOpen?.(false)

    const handleChange = (option: string) => {
        if (option === '') {
            setValue('')
            setIsAddingNewCostCenter(false)
            selectCostCenter(undefined)
        } else if (option === 'new') {
            setValue('new')
            setIsAddingNewCostCenter(true)
        } else {
            const costCenter = JSON.parse(option)
            setIsAddingNewCostCenter(false)
            setValue(option)
            selectCostCenter(costCenter.uuid)
            closeSelect()
        }
    }

    const handleFormCancel = () => {
        setValue(stringifyValue(defaultValue))
        setIsAddingNewCostCenter(false)
        closeSelect()
    }

    const handleFormSubmit = (newCostCenter: Omit<CostCenter, 'uuid'>) => {
        createNewCostCenter(newCostCenter)
        setIsAddingNewCostCenter(false)
        closeSelect()
    }

    return (
        <Stack direction="column" gap={theme.spacing(6)}>
            {usePiazzaInputs ? (
                <PiazzaAutocomplete
                    label={resolvedCopy.inputLabel}
                    value={value || undefined}
                    errorMessage={error ? '' : undefined}
                    options={[
                        { label: resolvedCopy.addNewLabel, value: 'new' },
                        ...options.map((option) => ({
                            label: joinDefinedStrings([option.name, option.description], ' | '),
                            value: JSON.stringify(option),
                        })),
                    ]}
                    onValueChange={(value) => handleChange(value ?? '')}
                />
            ) : (
                <Select
                    inputLabel={resolvedCopy.inputLabel}
                    error={error}
                    value={value}
                    options={[
                        { label: <TextWithIcon icon={<PlusCircle size={20} />} text={resolvedCopy.addNewLabel} />, value: 'new' },
                    ].concat(
                        options.map((costCenterOption) => ({
                            label: (
                                <Typography variant="bodyTextLarge">
                                    {joinDefinedStrings([costCenterOption.name, costCenterOption.description], ' | ')}
                                </Typography>
                            ),
                            value: JSON.stringify(costCenterOption),
                        }))
                    )}
                    selectProps={{
                        variant: 'outlined',
                        MenuProps: {
                            style: {
                                maxHeight: 400,
                            },
                        },
                    }}
                    clear={() => handleChange('')}
                    onChange={(e) => handleChange(e.target.value)}
                />
            )}
            {isAddingNewCostCenter && (
                <CostCenterForm onCancelCallback={handleFormCancel} onSubmitCallback={handleFormSubmit} labels={formLabels} />
            )}
        </Stack>
    )
}

const TextWithIcon = ({ text, icon }: { text: string; icon: ReactNode }) => {
    const theme = useTheme()
    return (
        <Stack direction="row" spacing={theme.spacing(4)} alignItems="center">
            <Box display="flex" height="100%" color={theme.palette.customtext.black54}>
                {icon}
            </Box>
            <TextWithEllipsis tooltipText={text} typographyProps={{ variant: 'bodyTextLarge', color: theme.palette.customtext.black100 }}>
                {text}
            </TextWithEllipsis>
        </Stack>
    )
}
